import { i18n } from './i18n.js';

/**
 * THE SIGN-UP CHALLENGE (owner decision, launch audit round 3, 2026-09-26:
 * fake accounts must cost something).
 *
 * When the server has Turnstile configured (`MoonfallEnv.turnstileSiteKey`), a
 * NEW account's signup ticket needs a Cloudflare Turnstile token, which the
 * server confirms with Cloudflare (game/utils/turnstile.js). Cloudflare's
 * script cannot be vendored, so it is loaded HERE, lazily, only while an
 * account is being created — never on an ordinary visit.
 *
 * The check runs out of sight and almost always passes in about a second; the
 * frame (consent-gate.css `.signup-challenge-overlay`) appears only when
 * Cloudflare asks the player to interact.
 */

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SIGNUP_ACTION = 'signup';
const CHALLENGE_TIMEOUT_MS = 3 * 60 * 1000;
let scriptPromise = null;

function uiText(key, fallback) {
  const translated = i18n?.t?.(key);
  return translated && translated !== key ? translated : fallback;
}

function challengeFailure(code, message) {
  return Object.assign(new Error(message), { code, data: { code, message } });
}

/** Cloudflare's global, once its script has loaded. */
function turnstileGlobal() {
  return /** @type {any} */ (window).turnstile;
}

function loadTurnstileScript() {
  if (turnstileGlobal()?.render) return Promise.resolve(turnstileGlobal());
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => (turnstileGlobal()?.render ? resolve(turnstileGlobal()) : reject(new Error('turnstile_missing')));
    script.onerror = () => reject(new Error('turnstile_script_failed'));
    document.head.appendChild(script);
  }).catch((error) => {
    // A failed load (a content blocker, a flaky network) may be retried.
    scriptPromise = null;
    throw error;
  });
  return scriptPromise;
}

/** The configured site key, or null when the server runs without the challenge. */
function resolveSignupChallengeSiteKey() {
  const siteKey = window.MoonfallEnv?.turnstileSiteKey;
  return typeof siteKey === 'string' && siteKey ? siteKey : null;
}

/**
 * Resolves a one-use Turnstile token for the sign-up action, or `null` when
 * the challenge is not configured. Rejects with `signup_challenge_unavailable`
 * when Cloudflare's check cannot load or run.
 */
async function obtainSignupChallengeToken() {
  const siteKey = resolveSignupChallengeSiteKey();
  if (!siteKey) return null;

  let turnstile;
  try {
    turnstile = await loadTurnstileScript();
  } catch (_) {
    throw challengeFailure(
      'signup_challenge_unavailable',
      uiText(
        'signupChallenge.loadFailed',
        "The sign-up check couldn't load. Turn off content blockers for this site, or try another browser.",
      ),
    );
  }

  const widget = document.createElement('div');
  widget.className = 'signup-challenge-widget';
  const modal = document.createElement('div');
  modal.className = 'consent-gate-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  const title = document.createElement('h2');
  title.className = 'consent-gate-title';
  title.textContent = uiText('signupChallenge.title', 'One quick check');
  const subtitle = document.createElement('p');
  subtitle.className = 'consent-gate-subtitle';
  subtitle.textContent = uiText('signupChallenge.subtitle', "Confirm you're a person to finish creating your account.");
  modal.append(title, subtitle, widget);
  const overlay = document.createElement('div');
  overlay.className = 'consent-gate-overlay signup-challenge-overlay';
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  let widgetId = null;
  try {
    return await new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(challengeFailure(
        'signup_challenge_unavailable',
        uiText('signupChallenge.timedOut', 'The sign-up check took too long. Please try again.'),
      )), CHALLENGE_TIMEOUT_MS);
      const settle = (fn) => (value) => { clearTimeout(timer); fn(value); };
      widgetId = turnstile.render(widget, {
        sitekey: siteKey,
        action: SIGNUP_ACTION,
        appearance: 'interaction-only',
        // Cloudflare picks the browser's language; its codes differ from ours.
        language: 'auto',
        callback: settle(resolve),
        'error-callback': settle(() => reject(challengeFailure(
          'signup_challenge_unavailable',
          uiText('signupChallenge.failed', "The sign-up check didn't pass. Please try again."),
        ))),
        'expired-callback': () => turnstile.reset?.(widgetId),
        'before-interactive-callback': () => overlay.classList.add('interactive'),
        'after-interactive-callback': () => overlay.classList.remove('interactive'),
      });
    });
  } finally {
    try { if (widgetId !== null) turnstile.remove?.(widgetId); } catch (_) { /* already gone */ }
    overlay.remove();
  }
}

export { obtainSignupChallengeToken, resolveSignupChallengeSiteKey };
